import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReadFlights.css';
import UploadTelemetryModal from './UploadTelemetryModal';
import UploadVideoModal from './UploadVideoModal';
import { calculateChildCompletion, getTemplateName } from '../utils/templateValidation';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Reuse endpoints from ReadFlights for consistency
const READ_FLIGHTS_API_ENDPOINT = process.env.REACT_APP_READ_FLIGHTS_API_ENDPOINT ||
  'https://whzy23elj2.execute-api.eu-west-2.amazonaws.com/prod/readFlightData';

// Flight summaries endpoint (used in graphs page) to obtain richer flight metadata (date & launch time)
const FLIGHT_SUMMARIES_API_ENDPOINT = process.env.REACT_APP_FLIGHT_SUMMARIES_API_ENDPOINT ||
  'https://78q545moi7.execute-api.eu-west-2.amazonaws.com/flightSummaries-dev';

// This component contains ONLY the "Prior Notes" (right pane) portion of the legacy ReadFlights page.
// It deliberately omits graph/video/filter functionality so users can access a focused notes view.

function FlightNotes() {
  const [flights, setFlights] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [nextToken, setNextToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allFlightsLoaded, setAllFlightsLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Upload functionality state
  const [uploadMenuCard, setUploadMenuCard] = useState(null); // holds submissionId of open menu
  const [showTelemetryModal, setShowTelemetryModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [modalFlightUuid, setModalFlightUuid] = useState('');
  // Flight summaries mapping: flight_uuid -> { flight_date, flight_name, flight_location, launch_timestamp, launch_time }
  const [flightSummaries, setFlightSummaries] = useState({});
  const [templates, setTemplates] = useState([]); // For resolving child template names
  const [expandedChildTemplates, setExpandedChildTemplates] = useState({}); // Track expanded child templates {submissionId-fieldId: true/false}
  const [childSubmissionData, setChildSubmissionData] = useState({}); // Cache child submission data {submissionId: data}
  const [loadingChildData, setLoadingChildData] = useState({}); // Track loading state {submissionId: true/false}
  const fetchedChildSubmissions = useRef(new Set()); // Track which child submissions we've already fetched
  const processedFlightIds = useRef(new Set()); // Track which parent flights we've already checked for child refs
  const allSubmissionsByUuid = useRef({}); // Cache ALL submissions by UUID as we load them
  const [showArchived, setShowArchived] = useState(false); // Toggle to show/hide archived notes
  const navigate = useNavigate();
  // nav offset state removed (unused)

  // Fetch templates for child template name resolution
  useEffect(() => {
    const TEMPLATES_API_ENDPOINT = process.env.REACT_APP_TEMPLATES_API_ENDPOINT ||
      'https://68ysvrjdb7.execute-api.eu-west-2.amazonaws.com/formTemplates-dev';
    
    const fetchTemplates = async () => {
      try {
        const res = await authFetch(`${TEMPLATES_API_ENDPOINT}?latestOnly=true`);
        if (res.ok) {
          const data = await res.json();
          const normalised = data.map(item => {
            const template = item.template || item.payload?.template || item;
            return {
              ...template,
              version: item.version || template.version || 1
            };
          });
          setTemplates(normalised);
        }
      } catch (err) {
        console.error('Error fetching templates for FlightNotes:', err);
      }
    };
    
    fetchTemplates();
  }, []);

  // Fetch child data for preview fields
  useEffect(() => {
    if (flights.length === 0 || templates.length === 0) return;
    
    const fetchChildDataForPreview = async () => {
      const startTime = performance.now();
      
      // Find all child submissions that have preview fields and haven't been loaded yet
      const childSubmissionsToFetch = [];
      let hasNewFlights = false;
      
      flights.forEach(submission => {
        // Skip if we've already processed this flight
        if (processedFlightIds.current.has(submission.submissionId)) return;
        
        hasNewFlights = true;
        processedFlightIds.current.add(submission.submissionId);
        
        if (!submission.childTemplateRefs) return;
        
        submission.childTemplateRefs.forEach(ref => {
          if (!ref.submissionId) return;
          
          // Skip if already successfully cached
          if (childSubmissionData[ref.submissionId]) return;
          
          // Skip if currently being fetched
          if (fetchedChildSubmissions.current.has(ref.submissionId)) return;
          
          // Check if this child template has preview fields
          const childTemplate = templates.find(t => t.id === ref.templateId || t.templateId === ref.templateId);
          if (childTemplate && childTemplate.definition) {
            const hasPreviewFields = childTemplate.definition.some(def => def.showInParent);
            if (hasPreviewFields && !childSubmissionsToFetch.includes(ref.submissionId)) {
              childSubmissionsToFetch.push(ref.submissionId);
            }
          }
        });
      });
      
      if (!hasNewFlights) {
        // All flights have been processed before, skip entirely
        return;
      }
      
      console.log(`[fetchChildDataForPreview] ⏱️ Starting at ${new Date().toISOString()}`);
      
      if (childSubmissionsToFetch.length === 0) {
        console.log(`[fetchChildDataForPreview] ⏱️ No submissions to fetch, completed in ${(performance.now() - startTime).toFixed(2)}ms`);
        return;
      }
      
      const identifyTime = performance.now();
      console.log(`[fetchChildDataForPreview] ⏱️ Identified ${childSubmissionsToFetch.length} child submissions in ${(identifyTime - startTime).toFixed(2)}ms`);
      
      // First, check if any are already in our cache from paginated loading
      const stillNeedToFetch = [];
      childSubmissionsToFetch.forEach(submissionId => {
        if (allSubmissionsByUuid.current[submissionId]) {
          console.log(`[fetchChildDataForPreview] Found ${submissionId} in cache, no API call needed`);
          fetchedChildSubmissions.current.add(submissionId);
          setChildSubmissionData(prev => ({
            ...prev,
            [submissionId]: allSubmissionsByUuid.current[submissionId]
          }));
        } else {
          stillNeedToFetch.push(submissionId);
        }
      });
      
      const cacheCheckTime = performance.now();
      console.log(`[fetchChildDataForPreview] ⏱️ Cache check completed in ${(cacheCheckTime - identifyTime).toFixed(2)}ms - ${childSubmissionsToFetch.length - stillNeedToFetch.length} found in cache, ${stillNeedToFetch.length} need API calls`);
      
      if (stillNeedToFetch.length === 0) {
        console.log(`[fetchChildDataForPreview] ⏱️ All submissions found in cache! Total time: ${(performance.now() - startTime).toFixed(2)}ms`);
        return;
      }
      
      const apiStartTime = performance.now();
      console.log(`[fetchChildDataForPreview] ⏱️ Fetching ALL ${stillNeedToFetch.length} submissions in parallel...`);
      
      // Fetch ALL remaining submissions in parallel at once (no batching)
      await Promise.all(stillNeedToFetch.map(async (submissionId) => {
        const itemStartTime = performance.now();
        // Mark as being fetched
        fetchedChildSubmissions.current.add(submissionId);
        setLoadingChildData(prev => ({ ...prev, [submissionId]: true }));
        
        try {
          const response = await authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionId}`);
          const data = await response.json();
          const firstFetchTime = performance.now();
          
          console.log(`[fetchChildDataForPreview] ⏱️ Fetched page 1 for ${submissionId} in ${(firstFetchTime - itemStartTime).toFixed(2)}ms:`, data);
          
          // Cache all items from response
          if (data && data.items) {
            data.items.forEach(item => {
              if (item && item.uuid) {
                allSubmissionsByUuid.current[item.uuid] = item;
              }
            });
          }
          
          let matchedItem = allSubmissionsByUuid.current[submissionId];
          
          // If not found and there's a nextToken, fetch remaining pages in parallel
          let pageCount = 1;
          if (!matchedItem && data.nextToken) {
            const maxPages = 100;
            let currentNextToken = data.nextToken;
            const paginationStartTime = performance.now();
            
            // Fetch pages sequentially until found or max reached
            while (!matchedItem && currentNextToken && pageCount < maxPages) {
              const nextResponse = await authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionId}&nextToken=${encodeURIComponent(currentNextToken)}`);
              const nextData = await nextResponse.json();
              
              if (nextData && nextData.items) {
                nextData.items.forEach(item => {
                  if (item && item.uuid) {
                    allSubmissionsByUuid.current[item.uuid] = item;
                  }
                });
                matchedItem = allSubmissionsByUuid.current[submissionId];
              }
              
              currentNextToken = nextData.nextToken;
              pageCount++;
            }
            
            const paginationTime = performance.now() - paginationStartTime;
            console.log(`[fetchChildDataForPreview] ⏱️ Searched ${pageCount} page(s) for ${submissionId} in ${paginationTime.toFixed(2)}ms`);
          }
          
          const itemTotalTime = performance.now() - itemStartTime;
          if (matchedItem) {
            console.log(`[fetchChildDataForPreview] ✅ Found ${submissionId} in ${itemTotalTime.toFixed(2)}ms`);
            setChildSubmissionData(prev => ({
              ...prev,
              [submissionId]: matchedItem
            }));
          } else {
            console.warn(`[fetchChildDataForPreview] ❌ Could not find ${submissionId} after ${pageCount} pages (${itemTotalTime.toFixed(2)}ms)`);
            // Remove from fetched set so it can be retried later
            fetchedChildSubmissions.current.delete(submissionId);
          }
        } catch (error) {
          console.error(`Error fetching child submission ${submissionId}:`, error);
          // Remove from fetched set so it can be retried later
          fetchedChildSubmissions.current.delete(submissionId);
        } finally {
          setLoadingChildData(prev => ({ ...prev, [submissionId]: false }));
        }
      }));
      
      const apiTotalTime = performance.now() - apiStartTime;
      console.log(`[fetchChildDataForPreview] ⏱️ All ${stillNeedToFetch.length} API calls completed in ${apiTotalTime.toFixed(2)}ms`);
      
      const totalTime = performance.now() - startTime;
      console.log(`[fetchChildDataForPreview] ⏱️ 🏁 TOTAL TIME: ${totalTime.toFixed(2)}ms for ${childSubmissionsToFetch.length} submissions`);
    };
    
    fetchChildDataForPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights, templates]); // Removed childSubmissionData - using ref to track fetches instead

  // Log template_id matches when both datasets are available
  useEffect(() => {
    if (flights.length > 0 && Object.keys(flightSummaries).length > 0) {
      const summariesWithTemplateId = Object.values(flightSummaries).filter(s => s.template_id);
      const submissionsWithIds = flights.filter(f => f.submissionId);
      
      const matches = [];
      submissionsWithIds.forEach(submission => {
        const matchingSummaries = summariesWithTemplateId.filter(
          summary => summary.template_id === submission.submissionId
        );
        if (matchingSummaries.length > 0) {
          matches.push({
            submissionId: submission.submissionId,
            formTitle: submission.formTitle,
            matchingFlights: matchingSummaries.map(s => ({
              flight_name: s.flight_name,
              template_id: s.template_id,
              launch_timestamp: s.launch_timestamp
            }))
          });
        }
      });
    }
  }, [flights, flightSummaries]);

  // Fetch paginated flight submissions (same logic as ReadFlights)
  // NOTE: Avoid depending on isInitialLoad here so the function identity stays stable
  const fetchFlights = useCallback(async (token) => {
    setIsLoading(true);
    setError(null);
    let url = READ_FLIGHTS_API_ENDPOINT;
    if (token) {
      url += `?nextToken=${encodeURIComponent(token)}`;
    }
    try {
      const response = await authFetch(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch flights and parse error JSON.' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response (content-type: ${contentType}). First 200 chars: ${text.slice(0,200)}`);
      }
      const data = await response.json();
      
      // Cache ALL items by UUID for efficient child submission lookup
      data.items.forEach(item => {
        if (item && item.uuid) {
          allSubmissionsByUuid.current[item.uuid] = item;
        }
      });
      
      const newFlights = data.items.map((item, index) => {
        if (!item || typeof item.uuid === 'undefined' || typeof item.submissionTS === 'undefined') {
          console.error(`Invalid item at index ${index}`, item);
          return null;
        }
        const outerFormData = item.formData || {};
        const sectionsArr = Array.isArray(outerFormData.sections) ? outerFormData.sections : null;
        const legacyFormData = outerFormData.formData && typeof outerFormData.formData === 'object' ? outerFormData.formData : null;
        const formTitle = outerFormData.formTitle || 'Untitled Form';
        const submittedBy = outerFormData.submittedBy || 'Unknown User';
        const templateVersion = outerFormData.templateVersion || null;
        const flightUuidFromItem = outerFormData.flight_uuid || (legacyFormData && legacyFormData.flight_uuid) || item.flight_uuid || null;
        const templateIdFromItem = item.templateId || outerFormData.templateId || null; // Add templateId mapping
        const childTemplateRefsFromItem = outerFormData.childTemplateRefs || null; // Extract child template refs
        const parentSubmissionIdFromItem = item.parentSubmissionId || null; // Extract parent submission ID
        const archivedFromItem = item.archived || outerFormData.archived || false; // Extract archived status
        
        return {
          submissionId: item.uuid,
          submittedAt: parseInt(String(item.submissionTS), 10),
          formTitle,
          submittedBy,
          sections: sectionsArr,
          data: legacyFormData,
          flightUuid: flightUuidFromItem,
          templateId: templateIdFromItem, // Include templateId
          templateVersion,
          childTemplateRefs: childTemplateRefsFromItem, // Include child template refs
          parentSubmissionId: parentSubmissionIdFromItem, // Include parent submission ID
          archived: archivedFromItem, // Include archived status
        };
      }).filter(Boolean).filter(submission => !submission.parentSubmissionId); // Filter out child notes
      // Merge then sort descending by submittedAt to ensure consistent order
      setFlights(prev => {
        // Merge (append if paginating) then de-duplicate by submissionId to avoid repeats from double fetch
        const merged = token ? [...prev, ...newFlights] : newFlights;
        const uniqueMap = new Map();
        for (const f of merged) {
          if (!f || !f.submissionId) continue;
            // Keep the first occurrence (earliest in merged order) or replace? We'll keep the earliest fetched.
          if (!uniqueMap.has(f.submissionId)) uniqueMap.set(f.submissionId, f);
        }
        const deduped = Array.from(uniqueMap.values());
        const sortedFlights = deduped
          .filter(f => typeof f.submittedAt === 'number' && !Number.isNaN(f.submittedAt))
          .sort((a,b) => b.submittedAt - a.submittedAt);
        
        return sortedFlights;
      });
      setNextToken(data.nextToken);
      if (!data.nextToken) setAllFlightsLoaded(true);
    } catch (err) {
      if (isInteractiveAuthError(err)) return;
      console.error('Error fetching flights:', err);
      setError(err.message);
      setAllFlightsLoaded(true);
    } finally {
      setIsLoading(false);
      if (isInitialLoad) setIsInitialLoad(false);
    }
  // Intentionally exclude isInitialLoad from deps so fetchFlights identity stays stable and isn't recreated once state flips.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // no deps so identity is stable (isInitialLoad handled separately)
  // Initial load only once
  useEffect(() => { fetchFlights(null); }, [fetchFlights]);

  // Fetch flight summaries once (no filters yet; could add date range later if needed)
  useEffect(() => {
    let ignore = false;
    const fetchSummaries = async () => {
      try {
        const resp = await authFetch(FLIGHT_SUMMARIES_API_ENDPOINT);
        if (!resp.ok) throw new Error(`Flight summaries HTTP ${resp.status}`);
        const rows = await resp.json();
        if (!Array.isArray(rows)) return;
        const map = {};
        rows.forEach(r => {
          if (!r || !r.flight_uuid) return;
          map[r.flight_uuid] = {
            flight_date: r.flight_date || null,
            flight_name: r.flight_name || r.flightName || null,
            flight_location: r.flight_location || null,
            launch_timestamp: r.launch_timestamp != null ? Number(r.launch_timestamp) : null,
            // Some APIs may supply launch_time as HH:MM:SS or HH:MM. Keep raw; we'll parse later.
            launch_time: r.launch_time || null,
            template_id: r.template_id || null, // Include template_id for UUID-based matching
          };
        });
        if (!ignore) setFlightSummaries(map);
      } catch (e) {
        console.warn('Failed to fetch flight summaries (non-fatal):', e);
      }
    };
    fetchSummaries();
    return () => { ignore = true; };
  }, []);

  // Auto-load configuration (set false to revert to manual paging)
  const AUTO_LOAD_ALL = true;
  const PARALLEL_PAGE_REQUESTS = 3; // Number of pages to fetch in parallel

  // After initial page loads, if enabled, fetch all remaining pages as fast as possible
  useEffect(() => {
    if (!AUTO_LOAD_ALL) return;
    if (isInitialLoad) return; // wait until first page finished
    if (allFlightsLoaded) return;
    
    let cancel = false;
    
    const loadRemaining = async () => {
      console.log('[loadRemaining] ⏱️ Starting to load all remaining pages...');
      const startTime = performance.now();
      
      // Fire off multiple requests in parallel
      const pendingRequests = [];
      
      while (!cancel && !allFlightsLoaded) {
        // Get current token
        let currentToken = nextToken;
        if (!currentToken) break;
        
        // Fire off up to PARALLEL_PAGE_REQUESTS at a time
        while (pendingRequests.length < PARALLEL_PAGE_REQUESTS && currentToken && !cancel) {
          const tokenToFetch = currentToken;
          console.log(`[loadRemaining] ⏱️ Firing request with token: ${tokenToFetch.substring(0, 10)}...`);
          
          const requestPromise = fetchFlights(tokenToFetch)
            .then(() => {
              pendingRequests.splice(pendingRequests.indexOf(requestPromise), 1);
            })
            .catch(e => {
              console.error('[loadRemaining] Error fetching page:', e);
              pendingRequests.splice(pendingRequests.indexOf(requestPromise), 1);
            });
          
          pendingRequests.push(requestPromise);
          
          // Wait a tiny bit for nextToken to update
          await new Promise(resolve => setTimeout(resolve, 10));
          currentToken = nextToken;
        }
        
        // Wait for at least one request to complete before continuing
        if (pendingRequests.length > 0) {
          await Promise.race(pendingRequests);
        }
      }
      
      // Wait for all remaining requests to complete
      await Promise.all(pendingRequests);
      
      const totalTime = performance.now() - startTime;
      console.log(`[loadRemaining] ⏱️ 🏁 Loaded all pages in ${totalTime.toFixed(2)}ms`);
    };
    
    loadRemaining();
    return () => { cancel = true; };
  }, [AUTO_LOAD_ALL, isInitialLoad, allFlightsLoaded, nextToken, fetchFlights]);

  const handleLoadMore = () => {
    if (nextToken && !isLoading) fetchFlights(nextToken);
  };

  const toggleLogExpansion = (submissionId) => {
    setExpandedLogs(prev => ({ ...prev, [submissionId]: !prev[submissionId] }));
  };

  // Toggle child template expansion and fetch data if needed
  const toggleChildTemplate = async (expandKey, submissionId) => {
    if (!submissionId) return;
    
    const startTime = performance.now();
    const isExpanding = !expandedChildTemplates[expandKey];
    
    console.log('[toggleChildTemplate] ⏱️', { expandKey, submissionId, isExpanding, timestamp: new Date().toISOString() });
    
    setExpandedChildTemplates(prev => ({
      ...prev,
      [expandKey]: isExpanding
    }));
    
    // Fetch child submission data if expanding and not already cached
    if (isExpanding && !childSubmissionData[submissionId]) {
      // First check if it's in our UUID cache
      if (allSubmissionsByUuid.current[submissionId]) {
        const cacheHitTime = performance.now() - startTime;
        console.log(`[toggleChildTemplate] ⏱️ ✅ Found in cache in ${cacheHitTime.toFixed(2)}ms:`, submissionId);
        fetchedChildSubmissions.current.add(submissionId);
        setChildSubmissionData(prev => ({
          ...prev,
          [submissionId]: allSubmissionsByUuid.current[submissionId]
        }));
        return;
      }
      
      // Skip if already being fetched
      if (fetchedChildSubmissions.current.has(submissionId)) {
        console.log('[toggleChildTemplate] Already fetching:', submissionId);
        return;
      }
      
      const fetchStartTime = performance.now();
      console.log('[toggleChildTemplate] ⏱️ Fetching child data for:', submissionId);
      
      // Mark as being fetched
      fetchedChildSubmissions.current.add(submissionId);
      
      setLoadingChildData(prev => ({ ...prev, [submissionId]: true }));
      try {
        const response = await authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionId}`);
        const data = await response.json();
        const firstFetchTime = performance.now() - fetchStartTime;
        
        console.log(`[toggleChildTemplate] ⏱️ Fetched page 1 in ${firstFetchTime.toFixed(2)}ms:`, data);
        
        // Cache all items from response
        if (data && data.items) {
          data.items.forEach(item => {
            if (item && item.uuid) {
              allSubmissionsByUuid.current[item.uuid] = item;
            }
          });
        }
        
        let matchedItem = allSubmissionsByUuid.current[submissionId];
        
        // If not found and there's a nextToken, fetch remaining pages
        if (!matchedItem && data.nextToken) {
          const maxPages = 100;
          let currentNextToken = data.nextToken;
          let pageCount = 1;
          
          while (!matchedItem && currentNextToken && pageCount < maxPages) {
            console.log(`[toggleChildTemplate] Page ${pageCount + 1}: Not found yet, fetching next page for ${submissionId}`);
            const nextResponse = await authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionId}&nextToken=${encodeURIComponent(currentNextToken)}`);
            const nextData = await nextResponse.json();
            
            if (nextData && nextData.items) {
              nextData.items.forEach(item => {
                if (item && item.uuid) {
                  allSubmissionsByUuid.current[item.uuid] = item;
                }
              });
              matchedItem = allSubmissionsByUuid.current[submissionId];
            }
            
            currentNextToken = nextData.nextToken;
            pageCount++;
          }
          
          console.log(`[toggleChildTemplate] Searched ${pageCount} page(s) for ${submissionId}`);
        }
        
        const totalFetchTime = performance.now() - fetchStartTime;
        if (matchedItem) {
          console.log(`[toggleChildTemplate] ⏱️ ✅ Found matched item in ${totalFetchTime.toFixed(2)}ms:`, matchedItem);
          setChildSubmissionData(prev => ({
            ...prev,
            [submissionId]: matchedItem
          }));
        } else {
          console.warn(`[toggleChildTemplate] ⏱️ ❌ Could not find submission after ${totalFetchTime.toFixed(2)}ms:`, submissionId);
          // Remove from fetched set so it can be retried later
          fetchedChildSubmissions.current.delete(submissionId);
        }
      } catch (error) {
        const errorTime = performance.now() - fetchStartTime;
        console.error(`[toggleChildTemplate] ⏱️ ❌ Error after ${errorTime.toFixed(2)}ms:`, error);
        // Remove from fetched set so it can be retried later
        fetchedChildSubmissions.current.delete(submissionId);
      } finally {
        const totalTime = performance.now() - startTime;
        console.log(`[toggleChildTemplate] ⏱️ 🏁 Completed in ${totalTime.toFixed(2)}ms for ${submissionId}`);
        setLoadingChildData(prev => ({ ...prev, [submissionId]: false }));
      }
    } else {
      const skipTime = performance.now() - startTime;
      console.log(`[toggleChildTemplate] ⏱️ Skipping fetch in ${skipTime.toFixed(2)}ms - isExpanding:`, isExpanding, 'already cached:', !!childSubmissionData[submissionId]);
    }
  };

  const formatDateDDMMYYYY = (dateNum) => {
    if (!dateNum) return '';
    const dateObj = new Date(dateNum);
    const day = String(dateObj.getDate()).padStart(2,'0');
    const month = String(dateObj.getMonth()+1).padStart(2,'0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTimeDDMMYYYYHHMM = (dateNum) => {
    if (!dateNum) return '';
    const dateObj = new Date(dateNum);
    const day = String(dateObj.getDate()).padStart(2,'0');
    const month = String(dateObj.getMonth()+1).padStart(2,'0');
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2,'0');
    const mins = String(dateObj.getMinutes()).padStart(2,'0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  };

  // --- Timestamp / Date Derivation Helpers ---
  const parsePotentialDate = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === 'number') {
      // Distinguish seconds vs ms (assume epoch seconds if < 10^12)
      const num = val < 1e12 ? val * 1000 : val;
      const d = new Date(num);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return null;
      // Try direct Date.parse
      const parsed = Date.parse(trimmed);
      if (!Number.isNaN(parsed)) return new Date(parsed);
      // Try DD/MM/YYYY
      const dm = /^([0-3]?\d)[/.-]([0-1]?\d)[/.-](\d{4})$/.exec(trimmed);
      if (dm) {
        const [, dd, mm, yyyy] = dm;
        const d = new Date(Number(yyyy), Number(mm)-1, Number(dd));
        return Number.isNaN(d.getTime()) ? null : d;
      }
      // Try YYYY-MM-DD
      const ym = /^(\d{4})[-/](\d{2})[-/](\d{2})$/.exec(trimmed);
      if (ym) {
        const [, y, m, d] = ym;
        const date = new Date(Number(y), Number(m)-1, Number(d));
        return Number.isNaN(date.getTime()) ? null : date;
      }
      return null;
    }
    return null;
  };

  const parseHHMMOrHHMMSS = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    const s = raw.trim();
    const m1 = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
    if (!m1) return null;
    let [, hh, mm, ss] = m1;
    const h = Number(hh); const m = Number(mm); const sec = ss ? Number(ss) : 0;
    if ([h,m,sec].some(n => Number.isNaN(n))) return null;
    if (h > 23 || m > 59 || sec > 59) return null;
    return { hours: h, minutes: m, seconds: sec };
  };

  const combineDateAndTime = (dateObj, timeObj) => {
    if (!dateObj) return null;
    if (!timeObj) return dateObj;
    const d = new Date(dateObj.getTime());
    d.setHours(timeObj.hours, timeObj.minutes, timeObj.seconds || 0, 0);
    return d;
  };

  const findFieldValueByLabelFuzzy = (sections, labelNeedles) => {
    if (!Array.isArray(sections)) return null;
    const needles = labelNeedles.map(n => n.toLowerCase());
    for (const sec of sections) {
      if (!Array.isArray(sec.fields)) continue;
      for (const f of sec.fields) {
        if (!f || typeof f.label !== 'string') continue;
        const lab = f.label.toLowerCase();
        if (needles.some(n => lab.includes(n))) {
          return f.value;
        }
      }
    }
    return null;
  };

  // Derive display datetime for a submission. Preference order:
  // 1. Flight summary (matched via submission.flightUuid) using launch_timestamp OR (flight_date + launch_time)
  // 2. Form fields labelled like Start time / Flight start / Launch time and Date / Flight date within the submission sections
  // 3. Fallback: submission.submittedAt
  // Derive display datetime for a submission. Returns { date: Date, source: 'api'|'notes'|'legacy'|'submitted' }
  const deriveDisplayDate = (submission) => {
    if (!submission) return { date: null, source: 'submitted' };
    
    // Debug: Log submission structure to understand what's available
    // console.log('[FlightNotes][DeriveDisplayDate] Submission structure:', {
    //   submissionId: submission.submissionId,
    //   flightUuid: submission.flightUuid,
    //   hasSections: !!submission.sections,
    //   sectionsCount: submission.sections?.length || 0,
    //   hasData: !!submission.data,
    //   submittedAt: submission.submittedAt
    // });
    
    // 1. Flight summary by DynamoDB uuid (template_id matching)
    if (submission.submissionId) {
      // Find flight summaries where template_id matches this DynamoDB uuid (form submission ID)
      const matchingFlights = Object.values(flightSummaries).filter(
        summary => summary.template_id === submission.submissionId
      );
      
      // console.log('[FlightNotes][DeriveDisplayDate] UUID-based flight search:', {
      //   submissionId: submission.submissionId,
      //   matchingFlightsCount: matchingFlights.length,
      //   matchingFlights: matchingFlights.map(f => ({ 
      //     flight_uuid: f.flight_uuid, 
      //     flight_name: f.flight_name, 
      //     launch_timestamp: f.launch_timestamp,
      //     template_id: f.template_id
      //   }))
      // });
      
      if (matchingFlights.length > 0) {
        // Use the most recent flight (highest launch_timestamp) for this form submission
        const mostRecentFlight = matchingFlights
          .filter(f => f.launch_timestamp != null && Number.isFinite(f.launch_timestamp))
          .sort((a, b) => b.launch_timestamp - a.launch_timestamp)[0];
          
        if (mostRecentFlight) {
          const tsMs = mostRecentFlight.launch_timestamp < 1e12 ? mostRecentFlight.launch_timestamp * 1000 : mostRecentFlight.launch_timestamp;
          const d = new Date(tsMs);
          if (!Number.isNaN(d.getTime())) {
            // console.log('[FlightNotes][DeriveDisplayDate] Using UUID-matched flight time:', d);
            return { date: d, source: 'api-uuid' };
          }
        }
      }
    }
    // 2. Form sections (notes) - try multiple field label patterns (avoid picking up names/teams)
    const timeRaw = findFieldValueByLabelFuzzy(submission.sections, [ 
      'start time', 'flight start', 'launch time', 'takeoff time'
    ]);
    const dateRaw = findFieldValueByLabelFuzzy(submission.sections, [ 
      'date', 'flight date', 'test date', 'launch date', 'takeoff date'
    ]);
    
    // Debug: Log what we found in the form sections
    // console.log('[FlightNotes][DeriveDisplayDate] Form sections search:', {
    //   timeRaw,
    //   dateRaw,
    //   sectionsStructure: submission.sections?.map(s => ({
    //     sectionLabel: s.sectionLabel,
    //     fieldLabels: s.fields?.map(f => ({ label: f.label, value: f.value }))
    //   }))
    // });
    
    const dateObj2 = parsePotentialDate(dateRaw);
    const timeObj2 = parseHHMMOrHHMMSS(typeof timeRaw === 'string' ? timeRaw : String(timeRaw || ''));
    
    // console.log('[FlightNotes][DeriveDisplayDate] Parsed date/time:', {
    //   dateObj2,
    //   timeObj2,
    //   dateRaw,
    //   timeRaw
    // });
    
    // Only accept notes source if BOTH date and time provided & parsed
    if (dateObj2 && timeObj2) {
      const combined2 = combineDateAndTime(dateObj2, timeObj2);
      if (combined2) {
        // console.log('[FlightNotes][DeriveDisplayDate] Using notes source:', combined2);
        return { date: combined2, source: 'notes' };
      }
    }
    // 3. Legacy data object
    if (submission.data && typeof submission.data === 'object') {
      const legacyDate = submission.data.date || submission.data.flight_date;
      const legacyTime = submission.data.start_time || submission.data.launch_time || submission.data.flight_start;
      const ld = parsePotentialDate(legacyDate);
      const lt = parseHHMMOrHHMMSS(typeof legacyTime === 'string' ? legacyTime : String(legacyTime || ''));
      if (ld && lt) {
        const combinedLegacy = combineDateAndTime(ld, lt);
        if (combinedLegacy) return { date: combinedLegacy, source: 'legacy' };
      }
    }
    // 4. Fallback to submitted timestamp
    return { date: new Date(submission.submittedAt), source: 'submitted' };
  };

  const formatDisplayDate = (dateObj) => {
    if (!dateObj) return '';
    return formatDateTimeDDMMYYYYHHMM(dateObj.getTime());
  };

  const sourceSymbol = (src) => {
    switch (src) {
      case 'api': return '🛰️';
      case 'api-uuid': return '🔗';
      case 'notes': return '📝';
      case 'legacy': return '📄';
      case 'submitted': return '⏱️';
      default: return '⏱️';
    }
  };

  const sourceLabel = (src) => {
    switch (src) {
      case 'api': return 'API Flight Time';
      case 'api-uuid': return 'Form-Linked Flight Time';
      case 'notes': return 'Notes Provided Time';
      case 'legacy': return 'Legacy Fields Time';
      case 'submitted': return 'Submission Time';
      default: return 'Submission Time';
    }
  };

  const getFieldValue = (key, value) => {
    if (typeof value === 'boolean') {
      return value ? <span className="boolean-true">Yes</span> : <span className="boolean-false">No</span>;
    }
    if (value === undefined || value === null || String(value).trim() === '') {
      return <span style={{ fontStyle: 'italic', color: '#999' }}>Not provided</span>;
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return value;
  };

  const getLocationFromSections = (submission) => {
    if (!submission || !Array.isArray(submission.sections)) return 'No location';
    const general = submission.sections.find(s => (s.sectionLabel || '').toLowerCase() === 'general');
    if (!general || !Array.isArray(general.fields)) return 'No location';
    const locField = general.fields.find(f => (f.label || '').toLowerCase() === 'location');
    const value = locField && locField.value;
    if (value === undefined || value === null || String(value).trim() === '') return 'No location';
    return String(value);
  };

  const getTestNameFromSections = (submission) => {
    if (!submission || !Array.isArray(submission.sections)) return 'No test name';
    const general = submission.sections.find(s => (s.sectionLabel || '').toLowerCase() === 'general');
    if (!general || !Array.isArray(general.fields)) return 'No test name';
    const field = general.fields.find(f => (f.label || '') === 'Test name');
    const value = field && field.value;
    if (value === undefined || value === null || String(value).trim() === '') return 'No test name';
    return String(value);
  };

  // Extract Fuselage ID (search any section fields whose label contains 'fuselage')
  const getFuselageIdFromSections = (submission) => {
    if (!submission || !Array.isArray(submission.sections)) return '';
    for (const sec of submission.sections) {
      if (!Array.isArray(sec.fields)) continue;
      const match = sec.fields.find(f => typeof f.label === 'string' && /fuselage/i.test(f.label));
      if (match) {
        const val = match.value;
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          return String(val);
        }
      }
    }
    // Also attempt legacy data object if present
    if (submission.data && typeof submission.data === 'object') {
      const legacyKey = Object.keys(submission.data).find(k => /fuselage/i.test(k));
      if (legacyKey) {
        const v = submission.data[legacyKey];
        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
      }
    }
    return ''; // return empty string so we can conditionally hide if absent
  };

  // Search text for client-side filtering
  const [searchText, setSearchText] = useState('');

  // Convert flightSummaries to flightOptions format for dropdown
  const flightOptions = useMemo(() => {
    const options = Object.entries(flightSummaries).map(([flight_uuid, summary]) => {
      const datePart = summary.flight_date ? String(summary.flight_date).slice(0, 10) : 'Unknown';
      const namePart = summary.flight_name || 'Unknown';
      const locPart = summary.flight_location || 'Unknown';
      return {
        value: flight_uuid,
        label: `${namePart} - ${locPart} - ${datePart}`,
        columns: [] // Video modal doesn't use columns, but keeping consistent with other components
      };
    });
    return options;
  }, [flightSummaries]);

  // Upload handler functions (openTelemetryModal removed as telemetry button now sets context directly)

  const closeTelemetryModal = () => setShowTelemetryModal(false);

  // Archive a submission (uses regular submission logic)
  const archiveSubmission = async (submissionId, currentArchivedStatus) => {
    const newArchivedStatus = !currentArchivedStatus;
    const confirmMessage = newArchivedStatus 
      ? 'Are you sure you want to archive this note? It will be hidden from the main view.' 
      : 'Are you sure you want to unarchive this note?';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const SUBMIT_ENDPOINT = process.env.REACT_APP_SUBMIT_FLIGHT_API_ENDPOINT || 
        'https://ivrm771szg.execute-api.eu-west-2.amazonaws.com/prod/submitFlight';
      
      // Fetch the current submission data
      const response = await authFetch(`${READ_FLIGHTS_API_ENDPOINT}?uuid=${submissionId}`);
      const data = await response.json();
      
      if (!data || !data.items || data.items.length === 0) {
        throw new Error('Submission not found');
      }
      
      const submission = data.items[0];
      const formData = submission.formData || {};
      
      // Simply update the archived flag in formData
      formData.archived = newArchivedStatus;
      
      // Use the standard UPDATE operation
      const updatedPayload = {
        uuid: submissionId,
        operation: 'UPDATE',
        formData: formData,
        submissionTS: submission.submissionTS,
        templateId: submission.templateId
      };
      
      const updateResponse = await authFetch(SUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedPayload)
      });
      
      if (!updateResponse.ok) {
        throw new Error('Failed to update archived status');
      }
      
      // Update local state
      setFlights(prev => prev.map(f => 
        f.submissionId === submissionId 
          ? { ...f, archived: newArchivedStatus } 
          : f
      ));
      
      alert(newArchivedStatus ? 'Note archived successfully' : 'Note unarchived successfully');
    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      console.error('Error archiving submission:', error);
      alert(`Failed to ${newArchivedStatus ? 'archive' : 'unarchive'} note: ${error.message}`);
    }
  };

  // Master stable sorted list (date desc, then submissionId for tie-break)
  const masterSortedFlights = useMemo(() => {
    return [...flights]
      .filter(f => typeof f.submittedAt === 'number')
      .sort((a,b) => {
        if (b.submittedAt !== a.submittedAt) return b.submittedAt - a.submittedAt;
        // tie-breaker: lexicographic submissionId (fallback empty string)
        return String(a.submissionId).localeCompare(String(b.submissionId));
      });
  }, [flights]);

  // Build a cached filtered list whenever flights, search text, or archived filter change
  const filteredFlights = useMemo(() => {
    // First filter by archived status
    const archivedFiltered = showArchived 
      ? masterSortedFlights 
      : masterSortedFlights.filter(f => !f.archived);
    
    const rawSearch = searchText.trim();
    if (!rawSearch) return archivedFiltered;
    const needle = rawSearch.toLowerCase();

    const gatherFields = (submission) => {
      const parts = [];
      parts.push(submission.formTitle || '');
      parts.push(String(submission.submissionId || ''));
      if (submission.submittedBy) parts.push(submission.submittedBy);
      if (submission.templateVersion) parts.push(`v${submission.templateVersion}`);
      if (submission.submittedAt) {
        parts.push(new Date(submission.submittedAt).toLocaleString());
        parts.push(formatDateDDMMYYYY(submission.submittedAt));
      }
      parts.push(getTestNameFromSections(submission));
      parts.push(getLocationFromSections(submission));
      if (Array.isArray(submission.sections)) {
        submission.sections.forEach(sec => {
          if (sec.sectionLabel) parts.push(sec.sectionLabel);
          if (Array.isArray(sec.fields)) {
            sec.fields.forEach(f => {
              if (f.label) parts.push(f.label);
              if (f.value !== undefined && f.value !== null) {
                try {
                  if (typeof f.value === 'object') {
                    parts.push(JSON.stringify(f.value));
                  } else {
                    parts.push(String(f.value));
                  }
                } catch (_) {
                  parts.push(String(f.value));
                }
              }
            });
          }
        });
      }
      if (submission.data && typeof submission.data === 'object') {
        try {
          parts.push(JSON.stringify(submission.data));
        } catch (_) {
          Object.entries(submission.data).forEach(([k,v]) => {
            parts.push(k);
            if (v !== undefined && v !== null) parts.push(String(v));
          });
        }
      }
      return parts.join('\n').toLowerCase();
    };

    return archivedFiltered.filter(sub => {
      try {
        return gatherFields(sub).includes(needle);
      } catch (e) {
        console.warn('Search filter error for submission', sub.submissionId, e);
        return true;
      }
    });
  }, [masterSortedFlights, searchText, showArchived]);

  useLayoutEffect(() => {
    // Measure combined height of main nav + this sub nav (if present)
    try {
      const root = document.documentElement;
      const mainNav = document.querySelector('nav'); // adjust selector if needed
      const subNav = document.querySelector('.sub-navbar');
      const mainNavH = mainNav ? mainNav.getBoundingClientRect().height : 0;
      const subNavH = subNav ? subNav.getBoundingClientRect().height : 0;
      const total = Math.round(mainNavH + subNavH);
  root.style.setProperty('--notes-nav-offset', total + 'px');
      // Recalculate on resize (debounced)
      let frame = null;
      const handle = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const mainNav2 = document.querySelector('nav');
          const subNav2 = document.querySelector('.sub-navbar');
            const h1 = mainNav2 ? mainNav2.getBoundingClientRect().height : 0;
            const h2 = subNav2 ? subNav2.getBoundingClientRect().height : 0;
            root.style.setProperty('--notes-nav-offset', Math.round(h1 + h2) + 'px');
        });
      };
      window.addEventListener('resize', handle);
      return () => { window.removeEventListener('resize', handle); if (frame) cancelAnimationFrame(frame); };
    } catch (_) { /* swallow */ }
  }, []);

  if (isInitialLoad && isLoading) {
    return <div className="read-flights-container"><p className="loading-message">Loading flight notes...</p></div>;
  }
  if (error && flights.length === 0) {
    return <div className="read-flights-container"><p className="error-message">Error: {error}</p></div>;
  }
  if (flights.length === 0 && !isLoading) {
    return <div className="read-flights-container"><div className="no-submissions">No flight notes found.</div></div>;
  }

  return (
    <>
      {/* Sub-navbar for flight notes actions */}
      <div className="sub-navbar" style={{ position:'relative' }}>
        <div className="sub-actions">
          <button
            className="sub-btn"
            onClick={() => navigate('/flight_tracker/write')}
          >Add Flight</button>
          <button
            className="sub-btn"
            onClick={() => navigate('/flight_tracker/templates')}
          >Customise Templates</button>
        </div>
        <div className="sub-search-wrapper">
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '10px', fontSize: '14px', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            Show Archived
          </label>
          <input
            type="text"
            className="sub-search-input"
            placeholder="Search notes..."
            value={searchText}
            onChange={(e)=>setSearchText(e.target.value)}
            aria-label="Search flight notes"
          />
        </div>
      </div>
    <div className="read-content-wrapper notes-layout-wide">
      <div className="read-page-container" style={{ justifyContent:'flex-start' }}>
        <div className="right-pane" style={{ flex: '0 0 100%' }}>
          <div className="read-flights-container">
            <h1>Prior Notes</h1>
            {error && <p className="error-message">Error fetching more flights: {error}</p>}
            {filteredFlights.map(submission => {
              const isExpanded = !!expandedLogs[submission.submissionId];
              const title = submission.formTitle || `Log ID: ${submission.submissionId}`;
              const locationText = getLocationFromSections(submission);
              const testName = getTestNameFromSections(submission);
              const fuselageId = getFuselageIdFromSections(submission);
              const { date: displayDateObj, source: timeSource } = deriveDisplayDate(submission);
              const displayDateStr = formatDisplayDate(displayDateObj);
              return (
                <div key={submission.submissionId} className={`submitted-form-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                  <div className="form-card-header" onClick={() => toggleLogExpansion(submission.submissionId)}>
                    <div className="header-meta">
                      <span className="meta-item meta-col test-name">{testName}</span>
                      {fuselageId && (
                        <span className="meta-item meta-col fuselage-id">{fuselageId}</span>
                      )}
                      <span className="meta-item meta-col submission-date" title={sourceLabel(timeSource)}>{sourceSymbol(timeSource)} {displayDateStr}</span>
                      <span className="meta-item meta-col location">{locationText}</span>
                    </div>
                    <div className="header-actions" onClick={(e)=>e.stopPropagation()}>
                      <button
                        className="expand-btn"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleLogExpansion(submission.submissionId)}
                      >{isExpanded ? '▾' : '▸'}</button>
                      <button
                        className="sub-btn edit-btn"
                        title="Edit this log"
                        onClick={() => {
                          console.log('🔄 EDIT BUTTON CLICKED from FlightNotes');
                          console.log('📝 Submission being passed to edit mode:', submission);
                          console.log('🎯 Navigation state being passed:', { mode:'flights', edit:true, submission });
                          navigate('/flight_tracker/write', { state: { mode:'flights', edit:true, submission } });
                        }}
                      >Edit</button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="form-card-body">
                      {/* Mini Flight Summary Panel */}
                      {submission.flightUuid && flightSummaries[submission.flightUuid] && (
                        <div className="mini-flight-summary" style={{ display:'flex', alignItems:'center', gap:'8px', background:'#f5f5f5', padding:'6px 8px', borderRadius:'4px', marginBottom:'8px', cursor:'pointer' }}
                          onClick={(e)=>{ 
                            e.stopPropagation(); 
                            const tn = getTestNameFromSections(submission);
                            // console.log('[FlightNotes][NavigateToGraphs]', {
                            //   flight_uuid: submission.flightUuid,
                            //   test_name: tn || 'N/A'
                            // });
                            navigate('/flight_tracker/graphs', { state: { preselectFlightUuid: submission.flightUuid, preselectFlightTestName: tn || null } }); 
                          }}
                          title="View graphs for this flight"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e)=>{ if (e.key==='Enter' || e.key===' ') { e.preventDefault(); const tn = getTestNameFromSections(submission); /* console.log('[FlightNotes][NavigateToGraphs][Key]', { flight_uuid: submission.flightUuid, test_name: tn || 'N/A' }); */ navigate('/flight_tracker/graphs', { state: { preselectFlightUuid: submission.flightUuid, preselectFlightTestName: tn || null } }); } }}
                        >
                          <span style={{ fontSize:'0.8rem', fontWeight:600 }}>Flight:</span>
                          <span style={{ fontSize:'0.8rem' }}>{flightSummaries[submission.flightUuid].flight_name || 'Unnamed'}</span>
                          {flightSummaries[submission.flightUuid].launch_timestamp != null && (
                            <span style={{ fontSize:'0.7rem', color:'#555' }}>LT: {formatDateTimeDDMMYYYYHHMM(flightSummaries[submission.flightUuid].launch_timestamp < 1e12 ? flightSummaries[submission.flightUuid].launch_timestamp * 1000 : flightSummaries[submission.flightUuid].launch_timestamp)}</span>
                          )}
                          {!flightSummaries[submission.flightUuid].launch_timestamp && flightSummaries[submission.flightUuid].flight_date && flightSummaries[submission.flightUuid].launch_time && (
                            <span style={{ fontSize:'0.7rem', color:'#555' }}>LT: {flightSummaries[submission.flightUuid].flight_date} {flightSummaries[submission.flightUuid].launch_time}</span>
                          )}
                          <span style={{ marginLeft:'auto', fontSize:'0.7rem', color:'#0074d9', textDecoration:'underline' }}>Open Graphs →</span>
                        </div>
                      )}
                      {/* Upload toolbar */}
                      <div className="upload-toolbar">
                        <button className="upload-menu-btn" onClick={(e)=>{e.stopPropagation(); setUploadMenuCard(uploadMenuCard===submission.submissionId? null: submission.submissionId);}}>Upload ▾</button>
                        <button
                          className="sub-btn archive-btn"
                          title={submission.archived ? 'Unarchive this log' : 'Archive this log'}
                          onClick={(e) => {e.stopPropagation(); archiveSubmission(submission.submissionId, submission.archived);}}
                          style={{ background: '#000', color: '#fff', marginLeft: '8px' }}
                        >{submission.archived ? 'Unarchive' : 'Archive'}</button>
                        {uploadMenuCard===submission.submissionId && (
                          <div className="upload-dropdown">
                            <button className="sub-btn" style={{ display:'block', width:'100%' }} onClick={(e)=>{
                              e.stopPropagation(); 
                              const contextToSet = { 
                                submissionId: submission.submissionId, 
                                templateId: submission.submissionId, // Use submission UUID for flight relationship
                                flightUuid: submission.flightUuid || null 
                              };
                              console.log('[FlightNotes][Telemetry Button Click] Setting upload context:', {
                                submissionId: submission.submissionId,
                                oldTemplateIdSlug: submission.templateId,
                                newTemplateIdUuid: submission.submissionId,
                                flightUuid: submission.flightUuid,
                                finalContext: contextToSet
                              });
                              setUploadMenuCard(contextToSet); 
                              setShowTelemetryModal(true);
                            }}>Telemetry</button>
                            <button className="sub-btn" style={{ display:'block', width:'100%' }} onClick={(e)=>{e.stopPropagation(); setModalFlightUuid(submission.flightUuid || submission.flight_uuid || ''); setShowVideoModal(true); setUploadMenuCard(null);}}>Video</button>
                          </div>
                        )}
                      </div>
                      
                      {/* Child Templates Status */}
                      {submission.childTemplateRefs && submission.childTemplateRefs.length > 0 && (
                        <div className="child-templates-section">
                          <hr className="read-section-divider" />
                          <div className="read-section-title">Sub-Templates</div>
                          <div className="child-templates-summary">
                            {(() => {
                              const { completed, total, percentage } = calculateChildCompletion(submission.childTemplateRefs);
                              return (
                                <div className="completion-bar-container">
                                  <div className="completion-text">
                                    {completed} of {total} completed ({percentage}%)
                                  </div>
                                  <div className="completion-bar">
                                    <div className="completion-bar-fill" style={{ width: `${percentage}%` }}></div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          {submission.childTemplateRefs.map((ref, refIdx) => {
                            const statusIcon = ref.status === 'completed' ? '✅' : ref.status === 'in-progress' ? '🟡' : '⚪';
                            const templateName = getTemplateName(ref.templateId, templates);
                            const expandKey = `${submission.submissionId}-${ref.fieldId}`;
                            const isExpanded = expandedChildTemplates[expandKey] || false;
                            const childData = ref.submissionId ? childSubmissionData[ref.submissionId] : null;
                            
                            // Get child template to find preview fields
                            const childTemplate = templates.find(t => t.id === ref.templateId || t.templateId === ref.templateId);
                            
                            // Extract preview fields (fields marked with showInParent: true)
                            const getPreviewFields = () => {
                              if (!childData || !childData.formData || !childTemplate || !childTemplate.definition) {
                                return [];
                              }
                              
                              const previewFieldDefs = childTemplate.definition.filter(def => def.showInParent);
                              const previewFields = [];
                              
                              // Find values for preview fields from child submission data
                              childData.formData.sections?.forEach(section => {
                                section.fields?.forEach(field => {
                                  const matchingDef = previewFieldDefs.find(def => def.label === field.label);
                                  if (matchingDef) {
                                    previewFields.push({
                                      label: field.label,
                                      value: field.value
                                    });
                                  }
                                });
                              });
                              
                              return previewFields;
                            };
                            
                            const previewFields = getPreviewFields();
                            
                            return (
                              <div key={refIdx} className="child-template-item-expandable">
                                <div 
                                  className="child-template-item-header"
                                  onClick={() => toggleChildTemplate(expandKey, ref.submissionId)}
                                  style={{ cursor: ref.submissionId ? 'pointer' : 'default' }}
                                >
                                  {ref.submissionId && <span className="expand-icon-small">{isExpanded ? '▼' : '▶'}</span>}
                                  <span className="child-template-status">{statusIcon}</span>
                                  <span className="child-template-name">{templateName}</span>
                                  {ref.required && <span className="required-badge-small">Required</span>}
                                  {ref.submissionId ? (
                                    <button 
                                      className="view-child-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('🔵 [FlightNotes] Edit child clicked:', {
                                          fieldId: ref.fieldId,
                                          submissionId: ref.submissionId,
                                          templateId: ref.templateId,
                                          fullRef: ref
                                        });
                                        // Navigate to view/edit child submission using URL params
                                        // This ensures WriteFlights fetches the submission data properly
                                        navigate(`/flight_tracker/write?edit=true&submissionId=${ref.submissionId}`);
                                      }}
                                    >
                                      Edit
                                    </button>
                                  ) : (
                                    <button 
                                      className="view-child-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('🔵 [FlightNotes] Create button clicked:', {
                                          templateId: ref.templateId,
                                          parentSubmissionId: submission.submissionId,
                                          parentFieldId: ref.fieldId,
                                          fullRef: ref
                                        });
                                        // Navigate to create child submission
                                        const childTemplate = templates.find(t => t.id === ref.templateId || t.templateId === ref.templateId);
                                        navigate('/flight_tracker/write', { 
                                          state: { 
                                            mode: 'flights',
                                            selectedTemplate: childTemplate?.id,
                                            parentSubmissionId: submission.submissionId,
                                            parentFieldId: ref.fieldId
                                          } 
                                        });
                                      }}
                                    >
                                      Create
                                    </button>
                                  )}
                                </div>
                                
                                {/* Show preview fields when collapsed and data is available */}
                                {!isExpanded && previewFields.length > 0 && (
                                  <div className="child-preview-fields">
                                    {previewFields.map((field, pfIdx) => (
                                      <div key={pfIdx} className="child-preview-field">
                                        <strong>{field.label}:</strong>
                                        <span>{field.value || 'N/A'}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {isExpanded && loadingChildData[ref.submissionId] && (
                                  <div className="child-template-data-inline" style={{ textAlign: 'center', padding: '20px' }}>
                                    <div className="loading-spinner"></div>
                                    <div style={{ marginTop: '10px', color: '#666' }}>Loading...</div>
                                  </div>
                                )}
                                
                                {isExpanded && !loadingChildData[ref.submissionId] && childData && childData.formData && (
                                  <div className="child-template-data-inline">
                                    {childData.formData.sections && childData.formData.sections.map((section, sIdx) => (
                                      <div key={sIdx} className="child-section-inline">
                                        {section.sectionLabel && (
                                          <div className="child-section-title">{section.sectionLabel}</div>
                                        )}
                                        {section.fields && section.fields.map((field, fIdx) => (
                                          <div key={fIdx} className="child-field-display">
                                            <strong>{field.label}:</strong>
                                            <span>{field.value || 'N/A'}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      <div className="form-field-display timestamp-detail">
                        <strong>Template Name:</strong>
                        <span>{title}</span>
                      </div>
                      {submission.templateVersion && (
                        <div className="form-field-display timestamp-detail">
                          <strong>Template Version:</strong>
                          <span>v{submission.templateVersion}</span>
                        </div>
                      )}
                      <div className="form-field-display timestamp-detail" title={sourceLabel(timeSource)}>
                        <strong>Flight Time:</strong>{' '}
                        <span>{sourceSymbol(timeSource)} {displayDateObj ? displayDateObj.toLocaleString() : new Date(submission.submittedAt).toLocaleString()}</span>
                      </div>
                      <div className="form-field-display">
                        <strong>Submitted By:</strong>
                        <span>{submission.submittedBy || 'N/A'}</span>
                      </div>
                      {submission.sections ? (
                        (() => {
                          // Find the template for this submission to get description fields
                          const submissionTemplate = templates.find(t => 
                            t.id === submission.templateId || t.templateId === submission.templateId
                          );
                          
                          return submission.sections.map((sec, idx) => {
                            // Find description fields that belong to this section from the template
                            let descriptionFields = [];
                            if (submissionTemplate && submissionTemplate.definition) {
                              let inTargetSection = false;
                              for (const def of submissionTemplate.definition) {
                                if (def.type === 'section') {
                                  inTargetSection = def.label === sec.sectionLabel;
                                } else if (inTargetSection && def.type === 'description') {
                                  descriptionFields.push(def);
                                }
                              }
                            }
                            
                            return (
                              <React.Fragment key={idx}>
                                <hr className="read-section-divider" />
                                <div className="read-section-title">{sec.sectionLabel}</div>
                                {/* Render description fields first */}
                                {descriptionFields.map((desc, dIdx) => (
                                  <div key={`desc-${dIdx}`} className="form-description-text" style={{marginBottom: '12px'}}>
                                    {desc.label}
                                  </div>
                                ))}
                                {/* Then render regular fields */}
                                {sec.fields.map((f, fi) => (
                                  <div key={fi} className="form-field-display">
                                    <strong>{f.label}:</strong>
                                    <span>{getFieldValue(f.label, f.value)}</span>
                                  </div>
                                ))}
                              </React.Fragment>
                            );
                          });
                        })()
                      ) : (
                        submission.data && Object.entries(submission.data).map(([key, value]) => {
                          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                          if (/Section$/i.test(key) && (value === undefined || value === null || String(value).trim() === '')) {
                            return (
                              <React.Fragment key={key}>
                                <hr className="read-section-divider" />
                                <div className="read-section-title">{label}</div>
                              </React.Fragment>
                            );
                          }
                          return (
                            <div key={key} className="form-field-display">
                              <strong>{label}:</strong>
                              <span>{getFieldValue(key, value)}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {searchText.trim() && filteredFlights.length === 0 && !isLoading && (
              <div className="no-submissions">No matches for "{searchText}".</div>
            )}
            {!AUTO_LOAD_ALL && !searchText.trim() && !allFlightsLoaded && !isLoading && (
              <button onClick={handleLoadMore} className="load-more-button">More Flights</button>
            )}
            {isLoading && !isInitialLoad && <p className="loading-message">Loading more flights...</p>}
          </div>
        </div>
      </div>
    </div>
    
  <UploadTelemetryModal isOpen={showTelemetryModal} onClose={closeTelemetryModal} context={uploadMenuCard && typeof uploadMenuCard === 'object' ? uploadMenuCard : null} />
    <UploadVideoModal
      open={showVideoModal}
      defaultFlightUuid={modalFlightUuid || ''}
      flightOptions={flightOptions}
      onClose={()=>setShowVideoModal(false)}
      onUploaded={()=>{
        // Could add refresh logic here if needed
        setShowVideoModal(false);
      }}
    />
    </>
  );
}

export default FlightNotes;
